import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Search, X, Download } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { downloadExcel } from '@/lib/downloadExcel';
import {
  getShopifyConfig, getShopifyStockPreview, setShopifySafety,
  type StockRow,
} from '@/api/shopify';

export function ShopifyStockPushPage() {
  const toast = useToast();
  const [search, setSearch] = useState('');

  // Live DRY_RUN / sync state — shown as a LIVE / Dry run chip in the filter row so
  // nobody pushes to the live store thinking it's a dry run (the toggles live on Settings).
  const cfgQuery = useQuery({ queryKey: ['shopify-config'], queryFn: getShopifyConfig });
  const cfg = cfgQuery.data;

  // Auto-load the preview so the table is always visible; Preview re-fetches on demand.
  // Search runs on the server with the same smart matcher as Warehouse Stock.
  const q = search.trim();
  const stockQuery = useQuery({
    queryKey: ['shopify-stock-preview', q],
    queryFn: () => getShopifyStockPreview(q || undefined),
    placeholderData: keepPreviousData,
  });
  const setSafety = useMutation({
    mutationFn: ({ sku, safetyStock }: { sku: string; safetyStock: number | null }) => setShopifySafety(sku, safetyStock),
    onSuccess: () => { stockQuery.refetch(); },
    onError: (e) => toast.push(apiError(e, 'Could not set safety stock'), 'error'),
  });
  const stock = stockQuery.data;
  const isLive = cfg?.dryRun === false;

  // Export the whole preview (no cap), honouring the applied search.
  function onExport() {
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    const qs = params.toString();
    downloadExcel(`/shopify/stock/export.xlsx${qs ? `?${qs}` : ''}`, 'GRHAMOY_Shopify_Stock_Push.xlsx', 120000)
      .catch((err) => toast.push(apiError(err, 'Export failed'), 'error'));
  }

  const filteredRows = stock?.rows ?? [];

  return (
    <div>
      <SectionHeader title="Ecommerce Inventory" />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:w-[320px]">
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" className="!pl-8 !pr-7" />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                aria-label="Clear search"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </FilterField>
        {/* Live / dry-run indicator — links to Settings, where the toggle lives. */}
        {cfg && (
          <Link
            to="/admin/configurations/shopify"
            className="self-center"
            title={isLive
              ? 'LIVE mode — pushing writes stock to the Shopify store. Click to change in Settings.'
              : 'Dry run is ON — pushes are logged only, nothing is written to Shopify. Click to change in Settings.'}
          >
            {isLive ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-danger/40 bg-danger/10 px-2.5 py-1 text-[11px] font-bold text-danger">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-danger opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-danger" />
                </span>
                LIVE
              </span>
            ) : (
              <Badge tone="muted">Dry run</Badge>
            )}
          </Link>
        )}
        {stock && (
          <span className="self-center"><Badge tone={stock.stockSyncEnabled ? 'success' : 'muted'}>Auto-sweep {stock.stockSyncEnabled ? 'on' : 'off'}</Badge></span>
        )}
        <Button variant="glass" size="sm" className="sm:ml-auto" icon={<Download size={13} />} onClick={onExport} title="Export the current search results to Excel (no row cap)">
          Export
        </Button>
      </FilterBar>

      <Table<StockRow>
        paginated pickerLast storageKey="shopify-stock" title="Stock Push → Shopify"
        titleAction={stock && (
          <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
            <span className="text-[var(--text-muted)]">Safety stock: <strong>{stock.safetyStock}</strong></span>
            <span className="text-[var(--text-muted)]">{stock.toPush} of {stock.total} would change</span>
            {stock.lastStockSyncAt && <span className="text-[var(--text-muted)]">Last sync: {new Date(stock.lastStockSyncAt).toLocaleString()}</span>}
            {!stock.locationId && <Badge tone="danger">No location set</Badge>}
          </div>
        )}
        count={stock ? (q ? `${filteredRows.length} of ${stock.total}` : `${stock.total}`) : ''}
        rows={filteredRows} rowKey={(r) => r.sku}
        loading={stockQuery.isLoading}
        empty={stockQuery.isError ? 'Could not load the stock preview. Reload the page to retry.' : 'No mapped, tracked variants to push.'}
        columns={[
          { key: 'img', header: 'Image', width: '56px', align: 'center', cell: (r) => <ItemThumb src={r.imageUrl} size={36} /> },
          { key: 'sku', header: 'SKU', cell: (r) => <span className="font-extrabold text-accent">{r.sku}</span> },
          { key: 'variant', header: 'Variant', cell: (r) => r.variantName || '—' },
          { key: 'item', header: 'ERP Item', cell: (r) => `${r.itemCode}` },
          { key: 'onhand', header: 'On-hand', align: 'right', cell: (r) => r.currentQty },
          { key: 'reserved', header: 'Reserved', align: 'right', cell: (r) => r.reservedQty },
          { key: 'safety', header: 'Safety', align: 'center', cell: (r) => (
            <SafetyCell row={r} globalSafety={stock?.safetyStock ?? 0} pending={setSafety.isPending}
              onSave={(v) => setSafety.mutate({ sku: r.sku, safetyStock: v })} />
          ) },
          { key: 'sellable', header: 'Sellable', align: 'right', cell: (r) => <strong>{r.sellable}</strong> },
          { key: 'last', header: 'Last pushed', align: 'right', cell: (r) => r.lastPushedQty ?? '—' },
          { key: 'push', header: 'Change', align: 'center', cell: (r) => (r.willPush
            ? <Badge tone="info">{r.lastPushedQty ?? '—'} → {r.sellable}</Badge>
            : <Badge tone="muted">in sync</Badge>) },
        ]}
      />
    </div>
  );
}

/** Inline editable per-variant safety-stock override. Empty = inherit the global default. */
function SafetyCell({ row, globalSafety, onSave, pending }: { row: StockRow; globalSafety: number; onSave: (v: number | null) => void; pending: boolean }) {
  const [val, setVal] = useState(row.safetyOverride == null ? '' : String(row.safetyOverride));
  useEffect(() => { setVal(row.safetyOverride == null ? '' : String(row.safetyOverride)); }, [row.safetyOverride]);
  function commit() {
    const t = val.trim();
    if (t !== '' && (Number.isNaN(Number(t)) || Number(t) < 0)) { setVal(row.safetyOverride == null ? '' : String(row.safetyOverride)); return; }
    const next = t === '' ? null : Math.floor(Number(t));
    if ((next ?? null) === (row.safetyOverride ?? null)) return; // unchanged
    onSave(next);
  }
  return (
    <input
      type="number" min={0} value={val} disabled={pending}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      placeholder={String(globalSafety)}
      title={row.safetyOverride == null ? `Using global default (${globalSafety})` : `Override: ${row.safetyOverride}`}
      className="w-16 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] px-1.5 py-0.5 text-center text-xs"
    />
  );
}
